import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VisitStatusChip } from "./Logs";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, RotateCcw, Send, Sparkles, Mail, Download, UserPlus, AlertTriangle, Ban } from "lucide-react";

const money = (x) => (x == null ? "—" : `$${Number(x).toFixed(2)}`);

function SharePanel({ v, onChange }) {
  const [recipients, setRecipients] = useState([]);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [busy, setBusy] = useState("");
  const share = v.share || {};
  const reviewed = share.reviewed_hash && share._current_hash === share.reviewed_hash;

  useEffect(() => {
    api.get(`/visits/${v.id}/share/recipients`).then((r) => setRecipients(r.data)).catch(() => {});
  }, [v.id]);

  const draft = async () => {
    setBusy("draft");
    try {
      const { data } = await api.post(`/visits/${v.id}/share/draft`);
      if (data.applied) { toast.success("School-facing summary drafted — review every word before sharing."); onChange(); }
      else toast("A suggestion is ready — see the candidate below."); onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const editShare = async (fields) => {
    try { await api.patch(`/visits/${v.id}/share`, fields); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const review = async () => {
    try { await api.post(`/visits/${v.id}/share/review`); onChange(); toast.success("Reviewed and approved to share — this is separate from billing approval."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {
    setBusy("send");
    try {
      const { data } = await api.post(`/visits/${v.id}/share/send`, { to, cc });
      const failed = (data.results || []).filter((r) => r.status !== "sent");
      if (!failed.length) toast.success(`Sent to ${data.results.length} recipient(s) — accepted by the email provider.`);
      else toast.error(`${data.results.length - failed.length} sent, ${failed.length} failed.`);
      onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="card p-6 space-y-4" data-testid="share-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif font-bold text-lg text-navy flex items-center gap-2"><Mail size={16} className="text-terracotta" /> School-facing visit summary</h2>
        {share.sent_status && share.sent_status !== "not_sent" && <span className="badge-approved" data-testid="share-sent-badge">Sent (provider-accepted) {share.sent_at?.slice(0, 10)}</span>}
      </div>
      <p className="text-xs text-[#57534E]">Goes to school staff you choose. Never includes private notes, coaching details, billing, or student names. Nothing sends automatically.</p>
      {!share.summary ? (
        <button className="btn-outline" onClick={draft} disabled={busy === "draft"} data-testid="share-draft-button">
          <Sparkles size={15} /> {busy === "draft" ? "Drafting…" : "Draft summary with AI"}</button>
      ) : (
        <>
          <div><label className="label">Subject</label>
            <input className="input" value={share.subject} data-testid="share-subject" onChange={(e) => editShare({ subject: e.target.value })} onBlur={onChange} /></div>
          <div><label className="label">Greeting</label>
            <input className="input" value={share.greeting} data-testid="share-greeting" onChange={(e) => editShare({ greeting: e.target.value })} onBlur={onChange} /></div>
          <div><label className="label">Summary</label>
            <textarea className="input min-h-[90px]" value={share.summary} data-testid="share-summary" onChange={(e) => editShare({ summary: e.target.value })} onBlur={onChange} /></div>
          <div><label className="label">Highlights (one per line)</label>
            <textarea className="input min-h-[70px]" value={(share.highlights || []).join("\n")} data-testid="share-highlights"
              onChange={(e) => editShare({ highlights: e.target.value.split("\n").filter(Boolean) })} onBlur={onChange} /></div>
          <div><label className="label">Next steps (one per line)</label>
            <textarea className="input min-h-[70px]" value={(share.nextSteps || []).join("\n")} data-testid="share-nextsteps"
              onChange={(e) => editShare({ nextSteps: e.target.value.split("\n").filter(Boolean) })} onBlur={onChange} /></div>
          <div><label className="label">Closing</label>
            <input className="input" value={share.closing} data-testid="share-closing" onChange={(e) => editShare({ closing: e.target.value })} onBlur={onChange} /></div>
          <div className="flex flex-wrap gap-2 items-center">
            <button className="btn-outline !py-1.5 text-xs" onClick={review} data-testid="share-review-button">
              <CheckCircle2 size={13} /> I've reviewed this — approve to share</button>
            {reviewed && <span className="badge-approved" data-testid="share-reviewed-badge">Reviewed</span>}
            {share.reviewed_hash && !reviewed && <span className="badge-nosend" data-testid="share-stale-badge">Edited since review — review again</span>}
            <button className="btn-ghost !py-1.5 text-xs" onClick={draft} disabled={busy === "draft"} data-testid="share-redraft"><Sparkles size={13} /> Redraft with AI</button>
          </div>
          <div className="rounded-lg border border-[#E7E1D4] p-4 space-y-2">
            <label className="label">To</label>
            <div className="flex flex-wrap gap-2" data-testid="share-to-list">
              {recipients.map((r) => (
                <label key={r.email} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer font-bold ${to.includes(r.email) ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`} data-testid={`share-to-${r.email}`}>
                  <input type="checkbox" className="hidden" checked={to.includes(r.email)}
                    onChange={(e) => setTo(e.target.checked ? [...to, r.email] : to.filter((x) => x !== r.email))} />
                  {r.name || r.email}
                </label>
              ))}
            </div>
            <label className="label">Cc</label>
            <div className="flex flex-wrap gap-2" data-testid="share-cc-list">
              {recipients.filter((r) => !to.includes(r.email)).map((r) => (
                <label key={r.email} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer ${cc.includes(r.email) ? "bg-terracotta text-white border-terracotta" : "bg-white text-navy border-[#E7E1D4]"}`} data-testid={`share-cc-${r.email}`}>
                  <input type="checkbox" className="hidden" checked={cc.includes(r.email)}
                    onChange={(e) => setCc(e.target.checked ? [...cc, r.email] : cc.filter((x) => x !== r.email))} />
                  {r.name || r.email}
                </label>
              ))}
            </div>
            <button className="btn-primary" onClick={send} disabled={busy === "send" || !to.length || !reviewed} data-testid="share-send-button">
              <Send size={14} /> {busy === "send" ? "Sending…" : "Send summary"}</button>
            {!reviewed && <span className="text-xs text-[#991B1B]">Review and approve to share before sending.</span>}
          </div>
          {(share.sent_to || []).length > 0 && (
            <div className="text-xs space-y-1" data-testid="share-sent-log">
              {share.sent_to.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className={s.status === "sent" ? "badge-approved" : "badge-nosend"}>{s.status}</span>
                  <span className="text-[#57534E]">{s.name || ""} &lt;{s.email}&gt;</span>
                  {s.detail && <span className="text-[#78716C]">{s.detail}</span>}
                  {s.error && <span className="text-[#991B1B]">{s.error}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function VisitDetail() {
  const { visitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState("");
  const [returnNote, setReturnNote] = useState("");
  const [reflTeacher, setReflTeacher] = useState("");
  const isAdmin = user?.role === "admin";

  const load = () => api.get(`/visits/${visitId}`).then((r) => setV(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/logs"); });
  useEffect(() => { load(); }, [visitId]);

  if (!v) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="visit-detail-loading">Loading visit…</div>;
  const st = v.computed_status;
  const act = async (action, body) => {
    setBusy(action);
    try {
      await api.post(`/visits/${visitId}/${action}`, body || {});
      await load();
      toast.success({ approve: "Approved for billing.", withdraw: "Withdrawn to draft.", return: "Returned to the consultant." }[action] || "Done.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const addReflection = async () => {
    if (!reflTeacher.trim()) return;
    setBusy("refl");
    try {
      const { data } = await api.post(`/visits/${visitId}/reflections`, { teacher: reflTeacher.trim() });
      navigate(`/reflections/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const fmtElapsed = (m) => (m == null ? "—" : `${Math.floor(m / 60)}h ${m % 60}m`);

  return (
    <div className="max-w-5xl space-y-5" data-testid="visit-detail-page">
      <div>
        <Link to="/logs" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-2" data-testid="back-to-logs"><ArrowLeft size={14} /> Daily Visit Logs</Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="section-title text-3xl" data-testid="visit-detail-title">{v.school?.name || "—"} · {v.date}</h1>
          <VisitStatusChip status={st} />
        </div>
        <p className="text-sm text-[#57534E]">{v.consultant_name}{v.elapsed_minutes != null ? ` · ${fmtElapsed(v.elapsed_minutes)} on site` : ""}{v.contract ? ` · ${v.contract.name}${v.contract.po ? ` · PO ${v.contract.po}` : ""}` : ""}</p>
      </div>

      {(v.billing_warnings || []).length > 0 && (
        <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 space-y-1" data-testid="detail-warnings">
          {v.billing_warnings.map((w, i) => <div key={i} className="text-xs text-[#92400E] flex gap-2"><AlertTriangle size={13} className="shrink-0 mt-0.5" />{w}</div>)}
        </div>
      )}
      {v.approval?.status === "returned" && v.approval.note && (
        <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3" data-testid="return-note">
          <div className="text-xs font-black text-[#991B1B]">Returned by {v.approval.decided_by}</div>
          <p className="text-sm text-[#7F1D1D]">{v.approval.note}</p>
        </div>
      )}

      <div className="card p-6 space-y-3" data-testid="visit-content-card">
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div><div className="label !mb-0">Grades</div><div className="font-bold text-navy">{v.grades || "—"}</div></div>
          <div><div className="label !mb-0">Time</div><div className="font-bold text-navy">{v.start || "?"}{v.end ? `–${v.end}` : ""}{v.break_minutes ? ` · ${v.break_minutes}m breaks` : ""}</div></div>
          <div><div className="label !mb-0">Supported</div><div className="font-bold text-navy">{(v.supported || []).map((p) => p.name).filter(Boolean).join(", ") || "—"}</div></div>
        </div>
        {v.purpose && <div><div className="label !mb-0">Purpose</div><p className="text-sm text-[#57534E]">{v.purpose}</p></div>}
        {(v.activities || []).length > 0 && (
          <div>
            <div className="label !mb-1">Activities</div>
            <table className="w-full text-sm">
              <thead className="bg-sand/60 text-xs"><tr><th className="table-th">Service</th><th className="table-th">Time</th><th className="table-th">With</th><th className="table-th">What happened</th></tr></thead>
              <tbody>
                {v.activities.map((a, i) => (
                  <tr key={i} data-testid={`detail-activity-${i}`}>
                    <td className="table-td font-bold">{a.type || "Activity"}</td>
                    <td className="table-td text-xs">{a.start}{a.end ? `–${a.end}` : ""}</td>
                    <td className="table-td text-xs">{a.participants}</td>
                    <td className="table-td text-xs">{a.description}{a.evidence ? <><br /><em className="text-[#78716C]">{a.evidence}</em></> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {v.summary && <div><div className="label !mb-0">Accomplishments</div><p className="text-sm text-[#57534E]">{v.summary}</p></div>}
        {v.evidence && <div><div className="label !mb-0">Evidence</div><p className="text-sm text-[#57534E]">{v.evidence}</p></div>}
        {(v.follow_ups || []).length > 0 && (
          <div><div className="label !mb-1">Follow-up</div>
            <ul className="text-sm space-y-1">{v.follow_ups.map((f, i) => <li key={i} data-testid={`detail-followup-${i}`}>• {f.text} — {f.owner}{f.due ? ` (due ${f.due})` : ""}</li>)}</ul></div>
        )}
      </div>

      <div className="card p-6 space-y-3" data-testid="billing-card">
        <h2 className="font-serif font-bold text-lg text-navy">Billing</h2>
        {(v.billing?.items || []).length ? v.billing.items.map((i, x) => (
          <div key={x} className="text-sm flex flex-wrap gap-2 items-center" data-testid={`billing-item-${x}`}>
            <strong>{i.qty} {i.unit}</strong> · {i.label}
            {isAdmin && i.rate != null && <span className="text-[#57534E]">· {money(i.rate)} → {money(i.qty * i.rate)}</span>}
          </div>
        )) : <p className="text-sm text-[#78716C]">No billable services selected.</p>}
        {v.billing?.confirmed ? <span className="badge-approved">Quantity confirmed by consultant</span> : <span className="badge-draft">Quantity not confirmed</span>}
        {v.billing?.note && <p className="text-xs text-[#78716C]">{v.billing.note}</p>}
        {isAdmin && v.amount != null && (
          <p className="text-sm">Amount at current rates: <strong data-testid="billing-amount">{money(v.amount)}</strong>
            {v.po_remaining != null && <span className="text-xs text-[#78716C]"> · PO remaining before this: {money(v.po_remaining)}</span>}</p>
        )}
        {v.approval?.invoice_number && <p className="text-xs text-[#78716C]">Invoice {v.approval.invoice_number}{v.approval.invoice_final ? " (final)" : " (draft)"}</p>}
      </div>

      <div className="card p-6 space-y-3" data-testid="actions-card">
        <h2 className="font-serif font-bold text-lg text-navy">Actions</h2>
        <div className="flex flex-wrap gap-2">
          {st === "draft" && <Link to={`/visit-work/${v.id}`} className="btn-outline" data-testid="edit-visit">Edit log</Link>}
          {st === "returned" && <Link to={`/visit-work/${v.id}`} className="btn-primary" data-testid="revise-visit">Revise and resubmit</Link>}
          {(st === "submitted" || st === "reapprove") && (v.is_owner || isAdmin) && (
            <button className="btn-ghost" onClick={() => act("withdraw")} disabled={!!busy} data-testid="withdraw-visit">
              <RotateCcw size={14} /> Withdraw to edit{st === "reapprove" ? " (needs approval again)" : ""}</button>
          )}
          {(st === "submitted" || st === "reapprove") && isAdmin && (
            <button className="btn-primary" onClick={() => act("approve")} disabled={!!busy || (v.approval_blocks || []).length > 0} data-testid="approve-visit">
              <CheckCircle2 size={15} /> {st === "reapprove" ? "Approve again for billing" : "Approve for billing"}</button>
          )}
          <a className="btn-ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/export`} target="_blank" rel="noreferrer" data-testid="export-html">School-facing preview</a>
          <a className="btn-ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/export.pdf`} target="_blank" rel="noreferrer" data-testid="export-pdf"><Download size={13} /> PDF</a>
        </div>
        {isAdmin && (v.approval_blocks || []).length > 0 && (st === "submitted" || st === "reapprove") && (
          <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3" data-testid="approval-blocks">
            <div className="text-xs font-black text-[#991B1B] mb-1">Can't approve yet</div>
            <ul className="text-xs text-[#991B1B] list-disc ml-4">{v.approval_blocks.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        {isAdmin && (st === "submitted" || st === "reapprove") && (
          <div className="flex flex-wrap gap-2 items-end pt-2 border-t border-[#E7E1D4]">
            <div className="flex-1 min-w-[240px]">
              <label className="label">Return with a revision note (required to return)</label>
              <textarea className="input min-h-[50px]" value={returnNote} data-testid="return-note-input" onChange={(e) => setReturnNote(e.target.value)} placeholder="What should the consultant change?" />
            </div>
            <button className="btn-outline text-[#991B1B]" onClick={() => act("return", { note: returnNote })} disabled={!!busy || !returnNote.trim()} data-testid="return-visit">
              <Ban size={14} /> Return to consultant</button>
          </div>
        )}
      </div>

      <div className="card p-6 space-y-3" data-testid="reflections-card">
        <h2 className="font-serif font-bold text-lg text-navy flex items-center gap-2"><UserPlus size={15} /> Teacher coaching summaries (TRU)</h2>
        <p className="text-xs text-[#57534E]">Optional reflections per teacher. Notes and analysis stay with the consultant and CKingEducation admins; the teacher receives only the reviewed feedback that is explicitly sent.</p>
        {(v.reflections || []).map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`reflection-row-${r.id}`}>
            <Link className="font-bold text-terracotta hover:underline" to={`/reflections/${r.id}`}>{r.teacher || "Teacher summary"}</Link>
            {r.has_feedback && <span className="badge-approved">Feedback drafted</span>}
            {r.share?.sent_status === "sent" && <span className="badge-neutral">Sent</span>}
          </div>
        ))}
        <div className="flex gap-2">
          <input className="input !py-1.5 text-sm flex-1 max-w-xs" placeholder="Teacher name or alias" value={reflTeacher} data-testid="new-reflection-teacher" onChange={(e) => setReflTeacher(e.target.value)} />
          <button className="btn-outline !py-1.5 text-xs" onClick={addReflection} disabled={busy === "refl" || !reflTeacher.trim()} data-testid="add-reflection">Start teacher summary</button>
        </div>
      </div>

      <SharePanel v={v} onChange={load} />

      <div className="card p-6" data-testid="history-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-2">History</h2>
        <ul className="space-y-1">{(v.history || []).map((h, i) => (
          <li key={i} className="text-xs text-[#57534E]" data-testid={`history-${i}`}><strong>{h.by || "—"}</strong> · {h.what} · {new Date(h.at).toLocaleString()}</li>
        ))}</ul>
      </div>
    </div>
  );
}
